<template>
    <client-only>
        <NuxtLink
            :to="urlToTermit"
            external
            target="_blank"
            class="!border-b-0 !no-underline"
        >
            <span ref="target">
                <span
                    class="termit"
                    @mouseover="op?.show"
                    @mouseout="op?.hide"
                    v-html="originText"
                />
            </span>
        </NuxtLink>
        <OverlayPanel
            ref="op"
            :pt
        >
            <div class="flex flex-col gap-2">
                <span
                    v-html="
                        termitDefinition?.definition?.[locale as LanguageCode]
                    "
                />
                <span
                    v-if="termitDefinition?.description"
                    v-html="
                        '&sup1; ' +
                        termitDefinition?.description?.[locale as LanguageCode]
                    "
                />
            </div>
        </OverlayPanel>
    </client-only>
</template>

<script lang="ts" setup>
import OverlayPanel from "primevue/overlaypanel"
import { useElementVisibility, watchOnce } from "@vueuse/core"
const { locale } = useI18n()

const ROOT_URL =
    "https://termit.fel.cvut.cz/ipr/sluzby/server/rest/public/vocabularies/"
const TERMIT_URL = "https://termit.fel.cvut.cz/ipr/#/public/vocabularies/"
const NAMESPACE = "https://slovník.gov.cz/generický/"

const props = defineProps<{
    text: string
    tema: TemaItem
}>()

const target = ref<HTMLElement | null>(null)
const targetIsVisible = useElementVisibility(target)
const op = ref<OverlayPanel | null>(null)

const termitDefinition = ref<TermitItem | undefined>(undefined)

watchOnce(targetIsVisible, async () => {
    if (targetIsVisible) {
        await getTermitDefinition()
    }
})

const REGEX_CONTENT = '<span[^>]*resource="([^"]+)"[^>]*>'
const REGEX_VOCABULARY = '<span[^>]*resource="([^"]+)"[^>]*>'
const REGEX_ORIGIN_TEXT = ">(.*)</span"
const term = props.text
    .match(new RegExp(REGEX_CONTENT))?.[1]
    ?.split("/")
    .reverse()[0]
const vocabulary = props.text
    .match(new RegExp(REGEX_VOCABULARY))?.[1]
    ?.split("/")[4]
const originText = props.text.match(new RegExp(REGEX_ORIGIN_TEXT))?.[1]

const urlToTermit = `${TERMIT_URL}${vocabulary}/terms/${term}?namespace=${encodeURIComponent(
    NAMESPACE
)}`

const termUri = `${ROOT_URL}${vocabulary}/terms/${term}?namespace=${encodeURIComponent(NAMESPACE)}`

async function getTermitDefinition() {
    const data = await $fetch<TermitItem>(termUri)
    termitDefinition.value = data
}

/* const correctedDefinition = computed(() => {
    const replaceSymbol = "&sup1;"
    if (!termitDefinition.value || !termitDefinition.value.definition) return ""
    const definition =
        termitDefinition.value.definition[locale.value as LanguageCode] ||
        termitDefinition.value.definition.cs
    const description =
        termitDefinition.value.description?.[locale.value as LanguageCode] ||
        termitDefinition.value.description?.cs
    if (description) {
        const definitionWithDescription = definition.replace(
            replaceSymbol,
            ` (${description.trim()})`
        )
        return definitionWithDescription
    }
    return definition
}) */

const pt = {
    root: {
        class: [
            // Shape
            "rounded-md shadow-lg max-w-[400px]",
            "border-0 ",

            // Position
            "absolute -translate-x-5 top-0 mt-2",
            "z-40 transform origin-center",

            // Color
            "bg-white ",
            "text-black-text ",
            "",

            // Before: Triangle
            "before:absolute before:-top-[9px] before:-ml-[9px] before:left-[calc(var(--overlayArrowLeft,0)+0.75rem)] z-0",
            "before:w-0 before:h-0",
            "before:border-transparent before:border-solid",
            "before:border-x-[4px] before:border-[4px]",
            "before:border-t-0 before:border-b-gray-300/10",

            "after:absolute after:-top-1 after:-ml-[4px] after:left-[calc(var(--overlayArrowLeft,0)+0.75rem)]",
            "after:w-0 after:h-0",
            "after:border-transparent after:border-solid",
            "after:border-x-[0.25rem] after:border-[0.25rem]",
            "after:border-t-0 after:border-b-black-text",
        ],
    },
    content: {
        class: "p-2 border text-sm items-center flex bg-white border border-black-text",
    },
    transition: {
        enterFromClass: "opacity-0 scale-y-[0.8]",
        enterActiveClass:
            "transition-[transform,opacity] duration-[120ms] ease-[cubic-bezier(0,0,0.2,1)]",
        leaveActiveClass: "transition-opacity duration-100 ease-linear",
        leaveToClass: "opacity-0",
    },
}
</script>

<style scoped></style>
